/**
 * `AddContractOptionDialog` — petit modal pour ajouter un rôle ou une
 * région custom depuis le formulaire de préparation de contrat.
 *
 * Deux modes :
 *   - kind="role"   → champ label + choix template_base (RD/RE)
 *   - kind="region" → champ label uniquement
 *
 * L'admin voit s'ouvrir le modal en cliquant sur le petit bouton "+" à
 * côté du dropdown. À la validation, la nouvelle entrée apparaît dans le
 * dropdown ET est automatiquement sélectionnée.
 */
import { Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";


export function AddContractOptionDialog({ kind, onCreated, createRole, createRegion }) {
    const [open, setOpen] = useState(false);
    const [label, setLabel] = useState("");
    const [templateBase, setTemplateBase] = useState("recruteur_donateurs");
    const [busy, setBusy] = useState(false);

    async function submit() {
        if (!label.trim() || label.trim().length < 2) {
            toast.error("Nom trop court (2 caractères minimum)");
            return;
        }
        setBusy(true);
        try {
            let created;
            if (kind === "role") {
                created = await createRole({ label: label.trim(), template_base: templateBase });
            } else {
                created = await createRegion({ label: label.trim() });
            }
            onCreated?.(created);
            setLabel("");
            setOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de l'ajout");
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    className="flex items-center justify-center h-9 w-9 rounded-lg border border-dashed border-shariti-orange text-shariti-orange hover:bg-shariti-orange-soft transition"
                    title={kind === "role" ? "Ajouter un nouveau rôle" : "Ajouter une nouvelle région"}
                    data-testid={`add-${kind}-btn`}
                >
                    <Plus className="h-4 w-4" />
                </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid={`add-${kind}-dialog`}>
                <DialogHeader>
                    <DialogTitle>
                        {kind === "role" ? "Ajouter un rôle de contrat" : "Ajouter une région"}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label htmlFor="option-label">
                            {kind === "role" ? "Nom du rôle" : "Nom de la région"}
                        </Label>
                        <Input
                            id="option-label"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder={kind === "role" ? "Ex : Recruteur expérimenté" : "Ex : Alsace"}
                            data-testid={`add-${kind}-label`}
                            onKeyDown={(e) => e.key === "Enter" && submit()}
                        />
                    </div>
                    {kind === "role" && (
                        <div>
                            <Label htmlFor="option-template">Modèle de contrat utilisé</Label>
                            <select
                                id="option-template"
                                value={templateBase}
                                onChange={(e) => setTemplateBase(e.target.value)}
                                data-testid="add-role-template-base"
                                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                            >
                                <option value="recruteur_donateurs">
                                    Comme un Recruteur de donateurs (taux horaire, temps partiel/plein)
                                </option>
                                <option value="responsable_equipe">
                                    Comme un Responsable d&apos;équipe (salaire mensuel fixe, temps plein)
                                </option>
                            </select>
                            <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                                Choisit quelle branche du template de contrat sera utilisée
                                pour ce nouveau rôle.
                            </p>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
                        Annuler
                    </Button>
                    <Button onClick={submit} disabled={busy} data-testid={`add-${kind}-confirm`}>
                        {busy ? "Ajout…" : "Ajouter"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
